import { useState, useEffect, useCallback, useRef } from 'react';
import { useApi } from '../hooks/useApi';
import { useAuth } from '../contexts/AuthContext';

const REFRESH_OPTIONS = [
  { label: 'Off',    value: 0   },
  { label: '30s',   value: 30  },
  { label: '1 min', value: 60  },
  { label: '2 min', value: 120 },
  { label: '5 min', value: 300 },
];

// Destinations as they appear in the DATABASE (tx_details_today / tr_logs going_to)
// Update this list to match your database values
const DB_DESTINATIONS = [
  'mediscor',
  'healthnet',
  'allegraconnect',
  'ipbm',
  'cimas',
  'apace',
  'mediswitch',
];

// Destinations as they appear in the LOG FILE (cpigws going_to field, col [3])
// Update this list to match your log file values
const LOG_DESTINATIONS = [
  'mdsphy',
  'healthnet',
  'iph',
  'ipbm',
  'cimas',
  'apace',
  'dhs',
  'dhsnam',
];

export default function Transactions() {
  const api = useApi();
  const { user } = useAuth();
  const today = new Date().toISOString().slice(0, 10);

  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState(null);
  const [source, setSource] = useState(null);
  const [rowsLoading, setRowsLoading] = useState(true);
  const [summaryLoading, setSummaryLoading] = useState(true);
  const [lastRefresh, setLastRefresh] = useState(null);
  const [refreshInterval, setRefreshInterval] = useState(60);
  const [countdown, setCountdown] = useState(0);
  const intervalRef  = useRef(null);
  const countdownRef = useRef(null);
  const [datasource, setDatasource] = useState(null);
  const [dsToggling, setDsToggling] = useState(false);

  // Fetch current datasource on mount
  useEffect(() => {
    api.get('/dashboard/datasource').then(d => setDatasource(d.datasource)).catch(() => {});
  }, [api]);

  const handleDsToggle = async () => {
    setDsToggling(true);
    try {
      const d = await api.post('/dashboard/datasource');
      setDatasource(d.datasource);
      setDestFilter(''); // clear destination filter — values differ between sources
      loadAll();
    } catch (e) { console.error(e); }
    finally { setDsToggling(false); }
  };

  const [date, setDate] = useState(today);
  const [mailboxFilter, setMailboxFilter] = useState('');
  const [destFilter, setDestFilter] = useState('');
  const [errorsOnly, setErrorsOnly] = useState(false);
  const [limit, setLimit] = useState(200);

  const buildParams = useCallback(() => {
    const p = new URLSearchParams({ date, limit });
    if (mailboxFilter) p.set('mailboxid', mailboxFilter);
    if (destFilter)    p.set('dest', destFilter);
    if (errorsOnly)    p.set('listerrors', '1');
    return p;
  }, [date, limit, mailboxFilter, destFilter, errorsOnly]);

  const loadRows = useCallback(async () => {
    setRowsLoading(true);
    try {
      const data = await api.get(`/dashboard/tr-logs?${buildParams()}`);
      setRows(data.rows || []);
      setSource(data.source || null);
    } catch (e) { console.error(e); }
    finally { setRowsLoading(false); }
  }, [api, buildParams]);

  const loadSummary = useCallback(async () => {
    setSummaryLoading(true);
    try {
      const sp = new URLSearchParams({ date });
      if (mailboxFilter) sp.set('mailboxid', mailboxFilter);
      if (destFilter)    sp.set('dest', destFilter);
      if (errorsOnly)    sp.set('listerrors', '1');
      setSummary(await api.get(`/dashboard/tr-summary?${sp}`));
    } catch (e) { console.error(e); }
    finally { setSummaryLoading(false); }
  }, [api, date, mailboxFilter, destFilter, errorsOnly]);

  const loadAll = useCallback(() => {
    loadRows();
    loadSummary();
    setLastRefresh(new Date());
  }, [loadRows, loadSummary]);

  useEffect(() => { loadAll(); }, [loadAll]);

  useEffect(() => {
    clearInterval(intervalRef.current);
    clearInterval(countdownRef.current);
    if (refreshInterval === 0) { setCountdown(0); return; }
    setCountdown(refreshInterval);
    countdownRef.current = setInterval(() => {
      setCountdown(c => c <= 1 ? refreshInterval : c - 1);
    }, 1000);
    intervalRef.current = setInterval(loadAll, refreshInterval * 1000);
    return () => {
      clearInterval(intervalRef.current);
      clearInterval(countdownRef.current);
    };
  }, [refreshInterval, loadAll]);

  const getStatusBadge = (r) => {
    const ok = (r.cpi_status === 0 || r.cpi_status === '0') &&
               (r.gateway_status === 0 || r.gateway_status === '0');
    return ok
      ? <span className="badge badge-success">OK</span>
      : <span className="badge badge-error">
          {r.cpi_status !== 0 && r.cpi_status !== '0'
            ? `CPI: ${r.cpi_status}` : `GW: ${r.gateway_status}`}
        </span>;
  };

  const isToday = date === today;

  return (
    <div className="animate-in">
      <div className="page-header">
        <div>
          <h1 className="page-title">Transactions</h1>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 4 }}>
            <div className="page-subtitle">Gateway transaction log</div>
            {source && (
              <span className={`badge ${source === 'logfile' ? 'badge-success' : 'badge-info'}`}>
                {source === 'logfile' ? '⬤ live log file' : '⬤ database'}
              </span>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          {/* Datasource toggle — L4 only */}
          {user?.accesslevel >= 4 && datasource && (
            <button
              className={`btn btn-sm ${datasource === 'log' ? 'btn-success' : 'btn-warning'}`}
              onClick={handleDsToggle}
              disabled={dsToggling}
              title={`Currently reading from ${datasource === 'log' ? 'log files' : 'database'}. Click to switch.`}
              style={{ fontSize: 11, padding: '4px 10px', fontFamily: 'var(--font-mono)' }}
            >
              {dsToggling ? '…' : `⬤ ${datasource.toUpperCase()}`}
            </button>
          )}
          {isToday && (
            <div style={{
              display: 'flex', alignItems: 'center', gap: 8,
              background: 'var(--bg-card)', border: '1px solid var(--border)',
              borderRadius: 6, padding: '6px 12px',
            }}>
              <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
                Auto-refresh
              </span>
              <select
                className="form-select"
                style={{ padding: '2px 6px', fontSize: 12, minWidth: 72 }}
                value={refreshInterval}
                onChange={e => setRefreshInterval(Number(e.target.value))}
              >
                {REFRESH_OPTIONS.map(o => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
              {refreshInterval > 0 && (
                <span style={{
                  fontSize: 11, fontFamily: 'var(--font-mono)',
                  color: countdown <= 10 ? 'var(--yellow)' : 'var(--text-muted)',
                  minWidth: 28, textAlign: 'right',
                }}>
                  {countdown}s
                </span>
              )}
            </div>
          )}
          {lastRefresh && (
            <span style={{ fontSize: 11, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>
              {lastRefresh.toLocaleTimeString()}
            </span>
          )}
          <button className="btn btn-secondary btn-sm" onClick={loadAll}>↻</button>
        </div>
      </div>

      <div className="card" style={{ marginBottom: 20 }}>
        <div style={{ display: 'flex', gap: 14, alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div className="form-group" style={{ minWidth: 160 }}>
            <label className="form-label">Date</label>
            <input className="form-input" type="date" value={date}
              onChange={e => setDate(e.target.value)} max={today} />
          </div>
          <div className="form-group" style={{ minWidth: 160 }}>
            <label className="form-label">Mailbox ID</label>
            <input className="form-input" placeholder="Filter by mailbox…"
              value={mailboxFilter} onChange={e => setMailboxFilter(e.target.value)} />
          </div>
          <div className="form-group" style={{ minWidth: 140 }}>
            <label className="form-label">Destination</label>
            <select className="form-select" value={destFilter} onChange={e => setDestFilter(e.target.value)}>
              <option value="">All destinations</option>
              {(datasource === 'log' ? LOG_DESTINATIONS : DB_DESTINATIONS).map(d => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Limit</label>
            <select className="form-select" value={limit} onChange={e => setLimit(e.target.value)}>
              {[100, 200, 500, 1000].map(v => <option key={v} value={v}>{v}</option>)}
            </select>
          </div>
          <div className="form-group">
            <label className="form-label">Errors only</label>
            <button
              className={`btn btn-sm ${errorsOnly ? 'btn-danger' : 'btn-ghost'}`}
              style={{ marginTop: 2 }}
              onClick={() => setErrorsOnly(v => !v)}
            >
              {errorsOnly ? '⚠ On' : '⚠ Off'}
            </button>
          </div>
          <button className="btn btn-primary" onClick={loadAll} style={{ marginBottom: 1 }}>Search</button>
        </div>
      </div>

      {/* Summary cards — always from tr-summary, never limited by display rows */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 12, marginBottom: 12 }}>
        {[
          { label: 'Total',      value: summary?.total,                         color: 'var(--accent)'  },
          { label: 'Successful', value: summary?.successful,                    color: 'var(--green)'   },
          { label: 'Failed',     value: summary?.failed,                        color: 'var(--red)'     },
          { label: 'Avg Time',   value: summary != null ? `${summary.avgTime} ms` : null, color: 'var(--yellow)' },
        ].map(s => (
          <div key={s.label} style={{
            background: 'var(--bg-card)', border: '1px solid var(--border)',
            borderRadius: 6, padding: '12px 16px',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
          }}>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{s.label}</span>
            {summaryLoading
              ? <div className="skeleton" style={{ width: 48, height: 16 }} />
              : <span style={{ fontFamily: 'var(--font-mono)', fontWeight: 700, color: s.color }}>
                  {s.value != null
                    ? (typeof s.value === 'number' ? s.value.toLocaleString() : s.value)
                    : '—'}
                </span>
            }
          </div>
        ))}
      </div>

      {!rowsLoading && rows.length > 0 && summary && (
        <div style={{ fontSize: 11, color: 'var(--text-muted)', marginBottom: 8 }}>
          Showing {rows.length.toLocaleString()} of {summary.total.toLocaleString()} transactions
          {summary.total > rows.length ? ' — increase limit or use filters to narrow results' : ''}
        </div>
      )}

      <div className="card" style={{ padding: 0 }}>
        <div className="table-container">
          <table className="data-table">
            <thead>
              <tr>
                <th>Date/Time</th>
                <th>Mailbox</th>
                <th>Destination</th>
                <th>Type</th>
                <th>Status</th>
                <th>CPI→GW</th>
                <th>Remote</th>
                <th style={{ color: 'var(--yellow)' }}>Total</th>
                <th>Error</th>
              </tr>
            </thead>
            <tbody>
              {rowsLoading ? (
                [...Array(8)].map((_, i) => (
                  <tr key={i}>{[...Array(9)].map((_, j) => (
                    <td key={j}><div className="skeleton" style={{ height: 12, width: '70%' }} /></td>
                  ))}</tr>
                ))
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={9} style={{ textAlign: 'center', color: 'var(--text-muted)', padding: '40px' }}>
                    {isToday ? "No transactions in today's log file yet" : 'No transactions found for this date'}
                  </td>
                </tr>
              ) : rows.map((row, i) => {
                const totalTime = row.totaltime ?? ((row.uptime || 0) + (row.remotetime || 0) + (row.downtime || 0));
                const cpiGwTime = (row.uptime || 0) + (row.downtime || 0);
                const isError = row.cpi_status !== 0 && row.cpi_status !== '0';
                return (
                  <tr key={i} style={isError ? { background: 'rgba(255,69,96,0.04)' } : {}}>
                    <td style={{ color: 'var(--text-primary)', whiteSpace: 'nowrap', fontFamily: 'var(--font-mono)', fontSize: 11 }}>
                      {new Date(row.date).toLocaleString()}
                    </td>
                    <td style={{ color: 'var(--accent)' }}>{row.mailboxname || row.source_mailbox}</td>
                    <td>{row.destination_mailbox}</td>
                    <td>{row.message_type || '—'}</td>
                    <td>{getStatusBadge(row)}</td>
                    <td style={{ color: 'var(--text-secondary)' }}>{cpiGwTime} ms</td>
                    <td style={{ color: 'var(--text-secondary)' }}>{row.remotetime || 0} ms</td>
                    <td style={{
                      fontWeight: 600,
                      color: totalTime > 5000 ? 'var(--red)' : totalTime > 2000 ? 'var(--yellow)' : 'var(--green)',
                    }}>
                      {totalTime} ms
                    </td>
                    <td style={{ color: 'var(--red)', fontSize: 11, maxWidth: 200 }}>
                      {row.error_message
                        ? <span title={row.error_message}>
                            {String(row.error_message).slice(0, 35)}
                            {String(row.error_message).length > 35 ? '…' : ''}
                          </span>
                        : '—'}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
